/**
 * Pure, framework-agnostic help-assistant chat logic (AB#26561). Ported verbatim (behaviour,
 * not syntax) from the React reference implementation so the Vue and React widgets agree on
 * how a streamed answer is split, what gets sent back to the server, and how timestamps are
 * displayed:
 *
 * - splitFollowups, toSendWindow, toolActivityLabel, formatMessageTimestamp: HelpAssistant.tsx
 *   (syrenis-frontends/packages/syrenis-ui/src/components/HelpAssistant/HelpAssistant.tsx)
 * - formatRelativeTime: ChatList.tsx (same directory)
 * - ratingsFromMessages: HelpAssistant.tsx, keyed by the server-assigned messageId rather than
 *   the React component's render-index (that indexing is an artefact of how the React list
 *   renders, not part of the wire contract - see chat-core-contract.md).
 *
 * See docs/work/AB26561-chat-docs-help-poc/chat-core-contract.md for the wire contract (SSE
 * events, REST DTOs, error-message maps, client rules) these functions assume.
 */

/**
 * @typedef {object} ChatMessage
 * @property {'user'|'assistant'} role
 * @property {string} content
 * @property {boolean} [isError] Rendered as a failed-turn bubble; never sent back to the server.
 */

/**
 * @typedef {object} StoredChatMessage
 * @property {number} messageId Server-assigned id.
 * @property {'user'|'assistant'} role
 * @property {string} content
 * @property {string} createdAt ISO 8601 timestamp.
 * @property {'up'|'down'|null} [rating]
 */

/** Marker after which trailing follow-up questions are appended to a streamed answer. */
export const FOLLOWUP_MARKER = '<<followups>>'

/**
 * Trailing-window cap on how many prior messages are sent with a chat request. Mirrors the
 * server's own ChatRequestValidator.MaxMessages cap (40) with headroom, so a client-added turn
 * can never tip a request over the server's own limit.
 */
export const MAX_SENT_MESSAGES = 30

/** Mirrors ChatRequestValidator.MaxMessageChars server-side. */
export const MAX_MESSAGE_CHARS = 4000

/**
 * Growth cap for the composers, about ten lines at an 18px line height. Matches `InputGroupTextarea`'s
 * default in syrenis-ui. Four lines, the old cap, left anyone drafting more than a couple of sentences
 * reading their own question through a scrollbar while the panel sat empty below it.
 */
export const COMPOSER_MAX_HEIGHT_PX = 200

/**
 * How much headroom is left when the composer's character counter appears. Silent until then: a
 * count on every keystroke is noise, and the field's `maxlength` cutting a long paste short with
 * no word was the problem being solved. Mirrors the React HelpAssistant's constant.
 */
export const COMPOSER_COUNTER_HEADROOM = 200

/**
 * What the composer shows about its length, or null while there is nothing worth showing.
 * @param {number} length Current draft length in characters.
 * @param {number} [max] The limit, `MAX_MESSAGE_CHARS` unless a caller has another.
 * @returns {{ text: string, status: string, atLimit: boolean } | null} `text` is the visible
 *   "3850/4000"; `status` is what a live region says, and changes only when the counter appears
 *   and again at the limit, so it announces those two moments and not every keystroke between;
 *   `atLimit` is true once the field will accept no more.
 */
export function composerCounter (length, max = MAX_MESSAGE_CHARS) {
	if (length < max - COMPOSER_COUNTER_HEADROOM) return null
	const atLimit = length >= max
	return {
		text: `${length}/${max}`,
		status: atLimit ? 'Character limit reached' : 'Approaching the character limit',
		atLimit
	}
}

/** Prefixes of scan-related tool names (Site Scanner tools); everything else is treated as a
 * config-inspection tool for the purposes of this hint. Matched with startsWith so a name like
 * `list_scans_for_brand` still counts. */
const SCAN_TOOL_PREFIXES = ['list_scans', 'query_scan', 'get_scan']

const MINUTE_MS = 60000
const HOUR_MS = 3600000
const DAY_MS = 86400000
const WEEK_MS = 604800000

/**
 * Splits streamed content into the visible answer body and trailing follow-up questions.
 * @param {string} text
 * @returns {{ body: string, followups: string[] }}
 */
export function splitFollowups (text) {
	const index = text.indexOf(FOLLOWUP_MARKER)
	if (index === -1) return { body: text, followups: [] }
	const followups = text
		.slice(index + FOLLOWUP_MARKER.length)
		.split('\n')
		// Strip a leading list marker only: a bullet, or a number followed by "." or ")" - not
		// any leading digits, so a question that itself starts with a year/number is untouched.
		.map(line => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
		.filter(Boolean)
		.slice(0, 3)
	return { body: text.slice(0, index).trimEnd(), followups }
}

/**
 * Collapses runs of consecutive same-role messages down to the LAST message of each run.
 * Bedrock Converse requires strict user/assistant alternation; dropping an error bubble (or the
 * abort-placeholder pop in run-turn.js) can leave two `user` messages adjacent to each other
 * (e.g. Stop pressed before the first delta, then a second question sent) or, less commonly, two
 * `assistant` messages adjacent.
 * @param {{ role: 'user'|'assistant', content: string }[]} messages
 * @returns {{ role: 'user'|'assistant', content: string }[]}
 */
function collapseConsecutiveSameRole (messages) {
	const collapsed = []
	for (const message of messages) {
		if (collapsed.length > 0 && collapsed[collapsed.length - 1].role === message.role) {
			collapsed[collapsed.length - 1] = message
		} else {
			collapsed.push(message)
		}
	}
	return collapsed
}

/**
 * Drops any leading `assistant` messages so the window starts with `user` - required by Bedrock
 * Converse alongside strict alternation.
 * @param {{ role: 'user'|'assistant', content: string }[]} messages
 * @returns {{ role: 'user'|'assistant', content: string }[]}
 */
function dropLeadingAssistant (messages) {
	let start = 0
	while (start < messages.length && messages[start].role === 'assistant') start++
	return messages.slice(start)
}

/**
 * Builds the trailing window of messages sent with a chat request: the newest
 * `MAX_SENT_MESSAGES` messages, error bubbles dropped, follow-up questions stripped from
 * content, and the result guaranteed to strictly alternate user/assistant starting (and ending)
 * with `user` - Bedrock Converse throws a ValidationException otherwise, and since the resulting
 * error bubble is itself filtered out on the next send, an unguarded violation wedges the chat
 * permanently (AB#26561 PR #2723 review).
 * @param {ChatMessage[]} messages
 * @returns {{ role: 'user'|'assistant', content: string }[]}
 */
export function toSendWindow (messages) {
	let window = messages.filter(message => !message.isError)
	window = collapseConsecutiveSameRole(window)
	window = dropLeadingAssistant(window)
	window = window.slice(-MAX_SENT_MESSAGES)
	// Re-check: the slice above can cut mid-pair and expose a new leading assistant message.
	window = dropLeadingAssistant(window)
	return window.map(({ role, content }) => ({ role, content: splitFollowups(content).body }))
}

/**
 * Friendly, transient label shown while a tool call is in flight.
 * @param {string} toolName
 * @returns {string}
 */
export function toolActivityLabel (toolName) {
	return SCAN_TOOL_PREFIXES.some(prefix => toolName.startsWith(prefix))
		? 'Checking your latest scan...'
		: 'Checking your configuration...'
}

/**
 * Formats a message timestamp for display in the user's local timezone: bare time
 * ("2:14 PM") for messages from today, or a short date prepended ("Jan 5, 2:14 PM") otherwise.
 * @param {string} iso ISO 8601 timestamp (server DTOs send UTC with a "Z" suffix).
 * @returns {string}
 */
export function formatMessageTimestamp (iso) {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return ''
	const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
	const isToday = date.toDateString() === new Date().toDateString()
	if (isToday) return time
	const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
	return `${day}, ${time}`
}

/**
 * "Just now" / "5m ago" / "3h ago" / "2d ago", falling back to a locale date past a week.
 * @param {string} iso
 * @param {number} [now] Defaults to `Date.now()`; injectable for tests.
 * @returns {string}
 */
export function formatRelativeTime (iso, now = Date.now()) {
	const then = new Date(iso).getTime()
	if (Number.isNaN(then)) return ''
	const diffMs = Math.max(0, now - then)
	if (diffMs < MINUTE_MS) return 'Just now'
	if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)}m ago`
	if (diffMs < DAY_MS) return `${Math.floor(diffMs / HOUR_MS)}h ago`
	if (diffMs < WEEK_MS) return `${Math.floor(diffMs / DAY_MS)}d ago`
	return new Date(iso).toLocaleDateString()
}

/**
 * Builds a ratings map (keyed by server-assigned messageId) from a fetched chat's stored
 * per-message ratings, so resuming/switching to a chat re-locks thumbs already rated instead of
 * rendering everything as fresh.
 * @param {StoredChatMessage[]} storedMessages
 * @returns {Object<number, 'up'|'down'>}
 */
export function ratingsFromMessages (storedMessages) {
	const ratings = {}
	storedMessages.forEach(message => {
		if (message.rating === 'up' || message.rating === 'down') ratings[message.messageId] = message.rating
	})
	return ratings
}
